import { useEffect, useState, useRef, useCallback } from "react";
import * as Highcharts from "highcharts";

// Hooks
import useChartResize from "../hooks/useChartResize";
import useChartRender from "../hooks/useChartRender";
import usePotentialHandlers from "./usePotentialHandlers";
import useChartSlice from "store/slice/charts/useChartSlice";
import useCustomiseLabels from "../hooks/useCustomiseLabels";
import useRenderWatermarkLabels from "../hooks/useRenderWatermarkLabels";

// Utils
import { getConfiguration, createLabel } from "utils/utils";

// Config
import { getOption } from "./PotentialConfig";

// Types
import {
    IChartAxisTitle,
    IRefObjectForHighchartsReact,
    IBaseChartProps,
    TPeopleDataElement,
    TPersonDataElement,
} from "components/Charts/chart.types";
import { LD6Chart, OptionsScatterSeries } from "store/slice/charts";

type TUsePotential = {
    chartOptions: OptionsScatterSeries;
    chartData: LD6Chart;
    chartRef: React.MutableRefObject<IRefObjectForHighchartsReact | null>;
    personDataElement: TPersonDataElement;
    peopleDataElement: TPeopleDataElement;
    containerRef: React.RefObject<HTMLDivElement> | null;
    showRoles: boolean;
    showNames: boolean;
    noUsersWithRealPaceData: boolean;
    showNoDataModal: boolean;
    onClose: () => void;
    setShowRoles: React.Dispatch<React.SetStateAction<boolean>>;
    setShowNames: React.Dispatch<React.SetStateAction<boolean>>;
    showAllLabels: boolean;
    setShowAllLabels: React.Dispatch<React.SetStateAction<boolean>>;
    hideAllLabels: boolean;
    setHideAllLabels: React.Dispatch<React.SetStateAction<boolean>>;
    onCloseNoDataModal: () => void;
};

const usePotential = ({ width, height, getData }: IBaseChartProps): TUsePotential => {
    const chartData = useChartSlice(getData) as LD6Chart;
    const [showRoles, setShowRoles] = useState(true);
    const [showNames, setShowNames] = useState(false);
    const [showAllLabels, setShowAllLabels] = useState(false);
    const [hideAllLabels, setHideAllLabels] = useState(false);

    const chartRef = useRef<IRefObjectForHighchartsReact>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const chartAxesTitles = useRef<IChartAxisTitle[]>([]);
    const legendSelected = useRef([...Array(chartData.series.length).keys()]);

    const noUsersWithRealPaceData = chartData.series.every((el) => !el.custom?.has_real_pace);

    const { renderWatermarkLabels } = useRenderWatermarkLabels(chartData);
    const { chartExtraOptions, resizeReRender, onChartResize } = useChartResize(width, height, "potential");
    const { load, personDataElement, peopleDataElement, onClose, onPointClick, legendItemClick } = usePotentialHandlers(
        chartData,
        chartAxesTitles,
        renderWatermarkLabels,
        legendSelected,
        containerRef
    );

    const [chartOptions, setChartOptions] = useState(
        getOption({ onClose, load, legendItemClick, onPointClick }, chartData)
    );

    useCustomiseLabels(
        showRoles,
        showNames,
        setChartOptions,
        legendSelected.current,
        showAllLabels,
        hideAllLabels,
        true
    );

    const [showNoDataModal, setShowNoDataModal] = useState<boolean>(false);

    useEffect(() => {
        const personSeries = chartData.series.filter((series) => series?.custom?.entity_type === "person");
        const hasNoData = personSeries.every((series) => Array.isArray(series.data) && series.data.length === 0);

        if (hasNoData) {
            setShowNoDataModal(true);
        }
    }, [chartData]);

    const onCloseNoDataModal = () => {
        setShowNoDataModal(false);
    };

    const renderAxesTitles = useCallback(
        (chart: Highcharts.Chart) => {
            const { plotLeft, plotTop, plotWidth, plotHeight } = chart;
            const { current: titles } = chartAxesTitles;

            if (titles.length === 0) {
                const {
                    xAxis: { title: titleXAxis },
                    yAxis: { title: titleYAxis },
                } = chartData;

                const optionsYAxis = getConfiguration(titleYAxis?.text ?? "", 0, 0);
                const optionsXAxis = getConfiguration(titleXAxis?.text ?? "", 0, 0);

                titles.push({
                    key: "yAxisTitle",
                    svg: createLabel(chart.renderer, optionsYAxis),
                });
                titles.push({
                    key: "xAxisTitle",
                    svg: createLabel(chart.renderer, optionsXAxis),
                });
            }

            titles.forEach((element: IChartAxisTitle) => {
                const labelBBox = element.svg.getBBox();

                if (element.key === "yAxisTitle") {
                    element.svg.attr({
                        x: plotWidth / 2 + plotLeft - labelBBox.width / 2,
                        y: plotHeight + plotTop,
                    });
                } else {
                    element.svg.attr({
                        x: 0,
                        y: plotHeight / 2 + plotTop - labelBBox.height / 2,
                    });
                }
            });
        },
        [chartData]
    );

    // Chart Render
    useChartRender(width, height, chartRef, [onChartResize, renderAxesTitles, renderWatermarkLabels]);

    useEffect(() => {
        const chart = chartRef.current?.chart;
        if (!chart?.renderer) return;

        // Set Chart Data
        setChartOptions(({ chart, credits, legend, xAxis, yAxis, plotOptions, ...currentOptions }) => ({
            ...currentOptions,
            chart: {
                ...chart,
                ...chartExtraOptions.current?.chart,
            },
            credits: {
                ...credits,
                position: {
                    ...credits?.position,
                    ...chartExtraOptions.current?.credits?.position,
                },
            },
            legend: {
                ...legend,
                ...chartExtraOptions.current?.legend,
            },
            series: chartData?.series,
            plotOptions: {
                series: {
                    ...plotOptions?.series,
                },
            },
        }));
    }, [chartData, width, height, chartExtraOptions, resizeReRender, renderWatermarkLabels]);

    return {
        chartOptions,
        chartData,
        chartRef,
        personDataElement,
        peopleDataElement,
        containerRef,
        showRoles,
        showNames,
        noUsersWithRealPaceData,
        showNoDataModal,
        showAllLabels,
        hideAllLabels,
        onClose,
        setShowRoles,
        setShowNames,
        setShowAllLabels,
        setHideAllLabels,
        onCloseNoDataModal,
    };
};

export default usePotential;
